import CustomLoadingButton from 'Components/CustomLoadingButton'
import StyledForm from 'Components/StyledForm'
import AllowedApiClients from 'Services/common/Features/AllowedApiClient/Components/AllowedApiClients'
import { useUpdateSingleCatalogMutation } from 'Services/ProductManagement/Features/Catalog/catalogApi'
import { SELECTED_CATALOG_PARAMETER_KEY } from 'Services/ProductManagement/Features/Catalog/Hooks/useCatalogsWithParams'
import type { Catalog } from 'Services/ProductManagement/Features/Catalog/types'
import { TariffCatalogTabType } from 'Services/ProductManagement/Features/Tariff/types'
import {
  DRAWER_TAB_PARAM_KEY,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PRODUCT_MANAGEMENT_ROOT_URL,
  Service
} from 'Util/constants'
import SnackbarUtils from 'Util/Snackbar'
import SaveIcon from '@mui/icons-material/Save'
import { Box, Button } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

interface TariffCatalogAllowedApiClientsProps {
  catalog: Catalog
  handleClose: () => void
}

function TariffCatalogAllowedApiClients({
  catalog,
  handleClose
}: Readonly<TariffCatalogAllowedApiClientsProps>) {
  const navigate = useNavigate()
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const { id: tariffId } = useParams()

  const [updateSingleCatalog, { isLoading: isSubmitLoading }] = useUpdateSingleCatalogMutation()

  const [selectedReadClients, setSelectedReadClients] = useState<string[]>(
    catalog?.api_client_read || []
  )
  const [selectedWriteClients, setSelectedWriteClients] = useState<string[]>(
    catalog?.api_client_write || []
  )

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!catalog) return

    updateSingleCatalog({
      id: catalog.id,
      api_client_read: selectedReadClients,
      api_client_write: selectedWriteClients
    })
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Sales channel successfully saved.`)
        if (IS_WALTTI_SERVICE_ENVIRONMENT) {
          navigate(
            `${baseUrl}/${tariffId}/sales-channels/${catalog.id}/products?${SELECTED_CATALOG_PARAMETER_KEY}=${catalog.id}&${DRAWER_TAB_PARAM_KEY}=${TariffCatalogTabType.ALLOWED_API_CLIENTS}`
          )
        } else {
          navigate(
            `${baseUrl}/${tariffId}/sales-channels?${SELECTED_CATALOG_PARAMETER_KEY}=${catalog.id}&${DRAWER_TAB_PARAM_KEY}=${TariffCatalogTabType.ALLOWED_API_CLIENTS}`
          )
        }
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error saving the sales channel.`)
      })
  }

  return (
    <StyledForm onSubmit={handleSave} isDisabled={isSubmitLoading}>
      <AllowedApiClients
        service={Service.PRODUCT_MANAGEMENT}
        selectedReadClients={selectedReadClients}
        selectedWriteClients={selectedWriteClients}
        setSelectedReadClients={setSelectedReadClients}
        setSelectedWriteClients={setSelectedWriteClients}
      />
      <Box
        sx={{
          mt: 2,
          mx: 2,
          display: 'flex',
          justifyContent: 'space-between',
          columnGap: 2
        }}
      >
        <Box display="flex" gap={2} sx={{ width: '100%' }}>
          <Button
            variant="outlined"
            size="medium"
            onClick={handleClose}
            color="inherit"
            sx={{ width: '100%', mt: 2 }}
          >
            {t`Cancel`}
          </Button>
          <CustomLoadingButton
            isLoading={isSubmitLoading}
            type="submit"
            icon={<SaveIcon />}
            size="medium"
            iconPosition="end"
            sx={{ width: '100%' }}
          >{t`Save changes`}</CustomLoadingButton>
        </Box>
      </Box>
    </StyledForm>
  )
}

export default TariffCatalogAllowedApiClients
